import { useLayoutEffect, useEffect, useRef, useCallback, useState } from 'react';
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';

import { useTranslation } from 'react-i18next';

import Dashboard from './pages/MainApp/Dashboard/Dashboard';
import Templates from './pages/MainApp/Templates/Templates';
import Integrations from './pages/MainApp/Integrations/Integrations';
import TeamsAccess from './pages/MainApp/TeamsAccess/TeamsAccess';
import Subscriptions from './pages/MainApp/Subscriptions/Subscriptions';
import GuidesShortcuts from './pages/MainApp/GuidesShortcuts/GuidesShortcuts';
import SupportFeedback from './pages/MainApp/SupportFeedback/SupportFeedback';
import Settings from './pages/MainApp/Settings/Settings';

import Diagram from './pages/Diagram/Diagram';
import WorkspaceOnboarding from './pages/Onboarding/WorkspaceOnboarding';

import SignIn from './pages/Auth/SignIn';
import SignUp from './pages/Auth/SignUp';

import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Invitation from './pages/Auth/Invitation';

import CheckEmail from './pages/Auth/CheckEmail/CheckEmail';
import EmailVerified from './pages/Auth/CheckEmail/EmailVerified';

import PasswordUpdated from './pages/Auth/CheckEmail/PasswordUpdated';

import Page404 from './pages/Auth/CheckEmail/Page404';
import PageError from './pages/Auth/CheckEmail/PageError';

import BillingSuccess from './pages/MainApp/BillingSuccess/BillingSuccess';
import SocketTest from './pages/SocketTest/SocketTest';

import { Seo } from './pages/Seo/Seo';

import { selectPreferencesIsInitialized } from './redux/preferences/preferences.selectors';
import { selectAuthUser, selectAuthSessionStatus } from './redux/auth/auth.selectors';
import { selectWorkspaceIsInitialized, selectWorkspaceList } from './redux/workspace/workspace.selectors';
import { checkUserSession } from "./redux/auth/auth.actions";

import LoaderLine from './components/Loaders/LoaderLine/LoaderLine';
import LoaderBar from './components/Loaders/LoaderBar/LoaderBar';
import SocketStatus from './components/SocketStatus/SocketStatus';

function SeoWrapper() {
    const { i18n } = useTranslation();
    const location = useLocation();

    return <Seo lang={i18n.language} path={location.pathname} />;
}

const App = () => {
    const history = useHistory();

    const { t, i18n } = useTranslation();

    const currentUser = useSelector(selectAuthUser);
    const sessionStatus = useSelector(selectAuthSessionStatus);
    const workspaceIsInitialized = useSelector(selectWorkspaceIsInitialized);
    const workspaceList = useSelector(selectWorkspaceList);

    // const isNotEmailVerified = false;//currentUser && !currentUser.emailVerified;
    // const isAuthorized = true;//currentUser && !isNotEmailVerified;

    const isNotEmailVerified = currentUser && !currentUser.emailVerified;
    const isAuthorized = currentUser && !isNotEmailVerified;
    const hasNoWorkspace = isAuthorized && workspaceIsInitialized && workspaceList.length === 0;
    // const hasNoWorkspace = true;

    const dispatch = useDispatch();
    const location = useLocation();
    const preferencesIsInitialized = useSelector(selectPreferencesIsInitialized);

    const isSessionChecked = useRef(false);

    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    useLayoutEffect(() => {
        if (!isSessionChecked.current) {
            isSessionChecked.current = true;
            dispatch(checkUserSession());
        }
    }, [dispatch])

    useLayoutEffect(() => {
        const isNotEmailVerified = currentUser && !currentUser.emailVerified;
        if (isNotEmailVerified) {
            const url = `/check-email?email=${currentUser.email}`;
            setTimeout(() => { history.push(url); }, 300);
        }
    }, [currentUser])


    // const [isLoader, setIsLoader] = useState(false);

    // useEffect(() => {
    //     setIsLoader(true);
    //     setTimeout(() => { setIsLoader(false) }, 800);
    // }, [])


    if (!sessionStatus) {
        return <div></div>; // Session check in flight
    }
    if (isAuthorized && (!workspaceIsInitialized || !preferencesIsInitialized)) {
        return <div></div>; // Workspace + preferences bootstrap in flight
    }

    return (
        <div className={`App dark-mode`}>
            <SeoWrapper />
            <SocketStatus />
            <Switch location={location} key={location.pathname}>

                {/* Dashboard Page */}
                <Route
                    // exact
                    path="/diagrams/"
                    render={() =>
                        isAuthorized
                            ? hasNoWorkspace
                                ? <WorkspaceOnboarding />
                                : <Dashboard />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                />

                {/* Templates Page */}
                {/* <Route
                    // exact
                    path="/templates/"
                    render={() =>
                        isAuthorized
                            ? <Templates />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Integrations Page */}
                {/* <Route
                    // exact
                    path="/integrations/"
                    render={() =>
                        isAuthorized
                            ? <Integrations />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Teams & Access Page */}
                {/* <Route
                    // exact
                    path="/teams-access/"
                    render={() =>
                        isAuthorized
                            ? <TeamsAccess />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Subscriptions Page */}
                {/* <Route
                    // exact
                    path="/subscriptions/"
                    render={() =>
                        isAuthorized
                            ? <Subscriptions />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Guides & Shortcuts Page */}
                {/* <Route
                    // exact
                    path="/guides-shortcuts/"
                    render={() =>
                        isAuthorized
                            ? <GuidesShortcuts />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Docs Page */}
                {/* <Route
                    // exact
                    path="/docs/"
                    render={() =>
                        isAuthorized
                            ? <Dashboard />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Invite Friends Page */}
                {/* <Route
                    // exact
                    path="/docs/"
                    render={() =>
                        isAuthorized
                            ? <Dashboard />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Upgrade Plan Page */}
                {/* <Route
                    // exact
                    path="/upgrade/"
                    render={() =>
                        isAuthorized
                            ? <Dashboard />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Support & Feedback */}
                {/* <Route
                    // exact
                    path="/support-feedback/"
                    render={() =>
                        isAuthorized
                            ? <SupportFeedback />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                /> */}

                {/* Settings */}
                <Route
                    // exact
                    path="/settings/:section?"
                    render={() =>
                        isAuthorized
                            ? hasNoWorkspace
                                ? <Redirect to="/diagrams/" />
                                : <Settings />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                />


                {/* Diagram Page */}
                <Route
                    exact
                    path="/d/:diagramId"
                    // render={() => isAuthorized ? <Diagram /> : <Redirect to={{ pathname: '/login/', state: { from: location } }} />}
                    render={() =>
                        isAuthorized
                            ? hasNoWorkspace
                                ? <Redirect to="/diagrams/" />
                                : <Diagram />
                            : isNotEmailVerified
                                ? <Redirect to={`/check-email?email=${currentUser.email}`} />
                                : <Redirect to="/login/" />
                    }
                />

                {/* Document Page */}
                {/* <Route path="/document/:documentId/" component={Document}></Route> */}

                {/* Subscriptions Page */}
                {/* <Route exact path="/subscriptions/" component={Subscriptions}></Route> */}

                {/* Account Page */}
                {/* <Route exact path="/account" component={Subscriptions}></Route> */}
                {/* <Route exact path="/profile" component={Subscriptions}></Route> */}
                {/* <Route exact path="/me" component={Subscriptions}></Route> */}

                {/* Auth Pages */}
                <Route
                    exact
                    path="/login"
                    // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignIn />}
                    component={SignIn}
                />
                <Route
                    exact
                    path="/signup"
                    // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignUp />}
                    render={() =>
                        // isAuthorized
                        //     ? <Redirect to="/diagrams/" />
                        //     : <SignUp />
                        <SignUp />
                    }
                />
                <Route
                    exact
                    path="/forgot-password"
                    render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <ForgotPassword />}
                />

                <Route
                    exact
                    path="/reset-password"
                    render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <ResetPassword />}
                />

                <Route
                    exact
                    path="/check-email"
                    component={CheckEmail}
                // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignUp />}
                />

                <Route
                    exact
                    path="/email-verified"
                    component={EmailVerified}
                // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignUp />}
                />

                <Route
                    exact
                    path="/password-updated"
                    component={PasswordUpdated}
                // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignUp />}
                />

                <Route
                    exact
                    path="/error"
                    component={PageError}
                // render={() => isAuthorized ? <Redirect to="/diagrams/" /> : <SignUp />}
                />

                {/* Workspace Invitation */}
                <Route
                    exact
                    path="/invite/:token/accept"
                    component={Invitation}
                />

                {/* Socket.IO Test Page */}
                {/* <Route exact path="/socket-test" component={SocketTest} /> */}

                {/* Billing Success Page */}
                <Route
                    exact
                    path="/upgrade/success"
                    component={BillingSuccess}
                // render={() =>
                //     isAuthorized
                //         ? <BillingSuccess />
                //         : <Redirect to="/login/" />
                // }
                />

                <Route
                    exact
                    path="/">
                    <Redirect to="/diagrams/" />
                </Route>

                {/* <Route
                    exact
                    path="/dashboard">
                    <Redirect to="/diagrams/" />
                </Route> */}

                {/* 404 Page Redirect */}
                <Route
                    path="/*"
                    component={Page404}
                />
            </Switch>
        </div>
    );
};

export default App;
